import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Loader2, Users, Layers, KeyRound, Save, Settings2, History } from "lucide-react";
import { toast } from "sonner";

export default function UsersPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [rows, setRows] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    role: "merchant",
    name: "", email: "", password: "", business_name: "",
    parent_id: "none", fee_percent: "", share_percent: "",
  });

  // --- manage (view/edit/password/active) ---
  const [manageOpen, setManageOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState({ name: "", business_name: "", fee_percent: "", share_percent: "" });
  const [newPassword, setNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [audit, setAudit] = useState([]);

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => setRows([]));

  useEffect(() => {
    load();
    if (isAdmin) api.get("/users", { params: { role: "distributor" } }).then((r) => setDistributors(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        role: form.role, name: form.name, email: form.email, password: form.password,
        business_name: form.business_name || form.name,
      };
      if (form.role === "merchant") {
        if (form.fee_percent) payload.fee_percent = parseFloat(form.fee_percent);
        if (isAdmin && form.parent_id !== "none") payload.parent_id = form.parent_id;
      }
      if (form.role === "distributor" && form.share_percent) payload.share_percent = parseFloat(form.share_percent);
      await api.post("/users", payload);
      toast.success(`${form.role === "merchant" ? "Merchant" : "Distributor"} created`);
      setOpen(false);
      setForm({ role: "merchant", name: "", email: "", password: "", business_name: "", parent_id: "none", fee_percent: "", share_percent: "" });
      load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openManage = async (u) => {
    setManageOpen(true);
    setDetail(null);
    setNewPassword("");
    setAudit([]);
    try {
      const { data } = await api.get(`/users/${u.id}`);
      setDetail(data);
      setEdit({
        name: data.name || "",
        business_name: data.business_name || "",
        fee_percent: data.fee_percent != null ? String(data.fee_percent) : "",
        share_percent: data.share_percent != null ? String(data.share_percent) : "",
      });
      api.get(`/users/${u.id}/audit`).then((r) => setAudit(r.data)).catch(() => setAudit([]));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setManageOpen(false);
    }
  };

  const refreshAudit = () => {
    if (detail) api.get(`/users/${detail.id}/audit`).then((r) => setAudit(r.data)).catch(() => {});
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { name: edit.name, business_name: edit.business_name };
      if (detail.role === "merchant" && edit.fee_percent !== "") payload.fee_percent = parseFloat(edit.fee_percent);
      if (detail.role === "distributor" && edit.share_percent !== "") payload.share_percent = parseFloat(edit.share_percent);
      await api.patch(`/users/${detail.id}`, payload);
      toast.success("Details updated");
      await refreshDetail();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.patch(`/users/${detail.id}`, { password: newPassword });
      toast.success(`Password changed for ${detail.name}`);
      setNewPassword("");
      refreshAudit();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleActiveDetail = async () => {
    setBusy(true);
    try {
      await api.patch(`/users/${detail.id}`, { active: !detail.active });
      toast.success(detail.active ? "Account deactivated" : "Account activated");
      await refreshDetail();
      refreshAudit();
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const refreshDetail = async () => {
    const { data } = await api.get(`/users/${detail.id}`);
    setDetail(data);
  };

  const toggleActive = async (u) => {
    await api.patch(`/users/${u.id}`, { active: !u.active });
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{isAdmin ? "Users & Network" : "My Merchants"}</h2>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Manage distributors and merchants across the platform." : "Onboard and manage your downline merchants."}
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button"><Plus className="mr-1.5 h-4 w-4" /> Add {isAdmin ? "user" : "merchant"}</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-dialog">
            <DialogHeader>
              <DialogTitle>Create account</DialogTitle>
              <DialogDescription>They'll sign in with these credentials.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-4">
              {isAdmin && (
                <div className="space-y-1.5">
                  <Label>Role</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="merchant">Merchant</SelectItem>
                      <SelectItem value="distributor">Distributor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="user-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Business name</Label>
                  <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="user-business-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="user-email-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="user-password-input" />
              </div>
              {form.role === "merchant" && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Platform fee %</Label>
                    <Input type="number" step="0.1" value={form.fee_percent} onChange={(e) => setForm({ ...form, fee_percent: e.target.value })}
                      placeholder="default" data-testid="user-fee-input" />
                  </div>
                  {isAdmin && (
                    <div className="space-y-1.5">
                      <Label>Distributor</Label>
                      <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
                        <SelectTrigger data-testid="user-parent-select"><SelectValue placeholder="None" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None (direct)</SelectItem>
                          {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}
              {form.role === "distributor" && (
                <div className="space-y-1.5">
                  <Label>Commission share % (of platform fee)</Label>
                  <Input type="number" step="0.1" value={form.share_percent} onChange={(e) => setForm({ ...form, share_percent: e.target.value })}
                    placeholder="default" data-testid="user-share-input" />
                </div>
              )}
              <DialogFooter>
                <Button type="submit" disabled={saving} data-testid="user-submit-button">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Email</TableHead>
              {isAdmin && <TableHead>Upline</TableHead>}
              <TableHead className="text-right">Rate</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-center">Active</TableHead>
              <TableHead className="text-right">Manage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {rows && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No users yet.</TableCell></TableRow>}
            {rows && rows.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-muted-foreground">{u.business_name}</div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={u.role === "distributor" ? "text-chart-2 border-chart-2/30 bg-chart-2/10" : "text-success border-success/30 bg-success/10"}>
                    {u.role === "distributor" ? <Layers className="h-3 w-3 mr-1" /> : <Users className="h-3 w-3 mr-1" />}
                    {u.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                {isAdmin && <TableCell className="text-sm text-muted-foreground">{u.parent_name || "—"}</TableCell>}
                <TableCell className="text-right font-mono text-sm">
                  {u.role === "merchant" ? `${u.fee_percent ?? "-"}% fee` : `${u.share_percent ?? "-"}% share`}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{dt(u.created_at)}</TableCell>
                <TableCell className="text-center">
                  {u.active
                    ? <span className="text-xs font-medium text-success">Active</span>
                    : <span className="text-xs font-medium text-destructive">Inactive</span>}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="outline" size="sm" onClick={() => openManage(u)} data-testid={`user-manage-${u.id}`}>
                    <Settings2 className="h-3.5 w-3.5 mr-1" /> Manage
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Manage dialog */}
      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-w-lg" data-testid="manage-user-dialog">
          <DialogHeader>
            <DialogTitle>Manage {detail?.name || "user"}</DialogTitle>
            <DialogDescription>View details, edit, change password, or activate / deactivate.</DialogDescription>
          </DialogHeader>
          {!detail ? (
            <div className="py-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
          ) : (
            <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
              {/* Details */}
              <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1.5" data-testid="manage-details">
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Email</span><span className="font-medium">{detail.email}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Role</span><span className="font-medium capitalize">{detail.role}</span></div>
                {detail.parent_name && <div className="flex justify-between gap-3"><span className="text-muted-foreground">Upline</span><span className="font-medium">{detail.parent_name}</span></div>}
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Status</span>
                  <span className={`font-medium ${detail.active ? "text-success" : "text-destructive"}`}>{detail.active ? "Active" : "Inactive"}</span></div>
                <div className="flex justify-between gap-3"><span className="text-muted-foreground">Created</span><span className="font-medium">{dt(detail.created_at)}</span></div>
                {detail.api_key && <div className="flex justify-between gap-3"><span className="text-muted-foreground">API key</span><span className="font-mono text-xs truncate max-w-[220px]">{detail.api_key}</span></div>}
              </div>

              {/* Stats */}
              {detail.stats && (
                <div className="grid grid-cols-3 gap-2 text-center" data-testid="manage-stats">
                  {detail.role === "distributor" ? (
                    <div className="rounded-lg border p-2"><div className="text-lg font-bold">{detail.stats.merchant_count}</div><div className="text-[11px] text-muted-foreground">Merchants</div></div>
                  ) : (
                    <div className="rounded-lg border p-2"><div className="text-lg font-bold">{detail.stats.vpa_count}</div><div className="text-[11px] text-muted-foreground">UPI IDs</div></div>
                  )}
                  <div className="rounded-lg border p-2"><div className="text-lg font-bold">{detail.stats.success_count}</div><div className="text-[11px] text-muted-foreground">Success txns</div></div>
                  <div className="rounded-lg border p-2"><div className="text-lg font-bold">₹{(detail.stats.success_volume || 0).toLocaleString("en-IN")}</div><div className="text-[11px] text-muted-foreground">Volume</div></div>
                </div>
              )}

              {/* Edit */}
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Name</Label>
                    <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="manage-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Business name</Label>
                    <Input value={edit.business_name} onChange={(e) => setEdit({ ...edit, business_name: e.target.value })} data-testid="manage-business-input" />
                  </div>
                </div>
                {detail.role === "merchant" && (
                  <div className="space-y-1.5">
                    <Label>Platform fee %</Label>
                    <Input type="number" step="0.1" value={edit.fee_percent} onChange={(e) => setEdit({ ...edit, fee_percent: e.target.value })} data-testid="manage-fee-input" />
                  </div>
                )}
                {detail.role === "distributor" && (
                  <div className="space-y-1.5">
                    <Label>Commission share %</Label>
                    <Input type="number" step="0.1" value={edit.share_percent} onChange={(e) => setEdit({ ...edit, share_percent: e.target.value })} data-testid="manage-share-input" />
                  </div>
                )}
                <Button onClick={saveEdit} disabled={busy} className="w-full" data-testid="manage-save-button">
                  <Save className="h-4 w-4 mr-1.5" /> Save changes
                </Button>
              </div>

              {/* Change password */}
              <div className="space-y-1.5 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-3">
                <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 text-amber-600" /> Change password</Label>
                <div className="flex gap-2">
                  <Input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password" data-testid="manage-password-input" />
                  <Button variant="secondary" onClick={changePassword} disabled={busy} data-testid="manage-password-button">Set</Button>
                </div>
                <p className="text-[11px] text-muted-foreground">The user will sign in with this new password immediately.</p>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label>Account active</Label>
                  <p className="text-[11px] text-muted-foreground mt-0.5">Deactivated users cannot log in.</p>
                </div>
                <Switch checked={detail.active} onCheckedChange={toggleActiveDetail} disabled={busy} data-testid="manage-active-switch" />
              </div>

              {/* Activity log */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5"><History className="h-3.5 w-3.5" /> Recent activity</Label>
                {audit.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No password or status changes recorded yet.</p>
                ) : (
                  <ul className="space-y-1.5 text-xs" data-testid="manage-audit-list">
                    {audit.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-3 rounded-md border px-2.5 py-1.5">
                        <span className="font-medium capitalize">{a.action.replace(/_/g, " ")}</span>
                        <span className="text-muted-foreground">by {a.actor_name} · {dt(a.at)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
